import { CommonModule, NgFor, NgClass } from '@angular/common'
import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core'

@Component({
  selector: 'app-pagination',
  standalone: true,
  imports: [NgFor, NgClass, CommonModule],
  styles: [
    `
      .link-disabled {
        color: gray;
        cursor: not-allowed;
        pointer-events: none;
      }
    `
  ],
  template: `
    <nav aria-label="Page navigation" class="pagination-style-3">
      <ul class="pagination justify-content-end me-3 mb-2">
        <li class="page-item">
          <a
            class="page-link"
            [ngClass]="{ 'link-disabled': currentPage === 1 }"
            href="javascript:void(0)"
            role="button"
            aria-label="Previous"
            (click)="setPageIfValid(currentPage - 1)"
          >
            <span aria-hidden="true"><i class="fas fa-chevron-left"></i></span>
            <span class="sr-only">Previous</span>
          </a>
        </li>

        <li
          *ngFor="let page of getPagesToShow(currentPage, totalPages)"
          class="page-item"
          [ngClass]="{ active: page === currentPage }"
        >
          <a
            *ngIf="page !== '...'"
            class="page-link"
            href="javascript:void(0)"
            role="button"
            (click)="setPageIfValid(page)"
            [attr.aria-current]="page === currentPage ? 'page' : null"
            >{{ page }}</a
          >
          <span *ngIf="page === '...'">...</span>
        </li>

        <li class="page-item">
          <a
            class="page-link"
            href="javascript:void(0)"
            role="button"
            [ngClass]="{ 'link-disabled': currentPage === totalPages }"
            aria-label="Next"
            (click)="setPageIfValid(currentPage + 1)"
          >
            <span aria-hidden="true"><i class="fas fa-chevron-right"></i></span>
            <span class="sr-only">Next</span>
          </a>
        </li>
      </ul>
    </nav>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PaginationComponent {
  @Input() currentPage!: number
  @Input() pageSize!: number
  @Input() totalRows!: number
  @Output() pageChange = new EventEmitter<number>()

  // Get total pages
  get totalPages(): number {
    return Math.ceil(this.totalRows / this.pageSize)
  }

  // Pages to show based on the current page and total pages
  getPagesToShow(current: number, total: number): (number | string)[] {
    const pages: (number | string)[] = []

    if (total <= 7) {
      return Array.from({ length: total }, (_, i) => i + 1)
    }

    pages.push(1)
    if (current > 3) pages.push('...')

    for (let i = current - 1; i <= current + 1; i++) {
      if (i > 1 && i < total) pages.push(i)
    }

    if (current < total - 2) pages.push('...')
    pages.push(total)

    return pages
  }

  // Set the current page
  setPage(page: number): void {
    if (page !== this.currentPage && page >= 1 && page <= this.totalPages) {
      this.pageChange.emit(page)
    }
  }

  // Ensure the page is a number before calling setPage
  setPageIfValid(page: number | string): void {
    if (typeof page === 'number') {
      this.setPage(page)
    }
  }
}
